import { string, object } from 'yup';
import { useQueryClient, useMutation, useQuery } from 'react-query';
import { pipe, xray, json, bodyOnly, fetch, Method } from '@ux/drivers';
import { authDriver, useIsAuthenticated } from '@ux/auth';

const ACCOUNT = '@@ux/accounts-client/ACCOUNT';
const ACCOUNT_DETAILS = '@@ux/accounts-client/ACCOUNT_DETAILS';
const PREFERENCES = '@@ux/accounts-client/PREFERENCES';
const PASSWORD_STRENGTH = '@@ux/accounts-client/PASSWORD_STRENGTH';

const useRefetchAccount = () => {
  const queryCache = useQueryClient();
  return _ref => {
    let {
      id
    } = _ref;
    queryCache.invalidateQueries([ACCOUNT, id]);
  };
};
var useRefetchAccount$1 = useRefetchAccount;

const getAccountApiUrl = () => {
  try {
    return ACCOUNT_API_URL;
  } catch (e) {
    return '/api/tyk/accounts';
  }
};
var apiUrl$1 = getAccountApiUrl;

const apiUrl = url => `${url}/v1/accounts/:id`;
function makeApiUrl() {
  return apiUrl(apiUrl$1());
}

function replaceUrl(_ref) {
  let {
    url,
    idReplacement = 'my',
    restUrl = ''
  } = _ref;
  return `${url.replace(':id', idReplacement)}${restUrl}`;
}

var makeDriver = (() => {
  return pipe(authDriver(), xray(), json(), bodyOnly())(fetch);
});
const jsonHeader = () => http => pipe(config => ({
  ...config,
  headers: {
    ...config.headers,
    'Content-Type': 'application/json'
  }
}), http);
const noContentDriver = () => pipe(authDriver(), xray(), jsonHeader())(fetch);

const fetchAccount = (url, driver) => {
  return _ref => {
    let {
      id
    } = _ref;
    return driver({
      url: replaceUrl({
        url,
        idReplacement: id
      })
    });
  };
};
function makeFetchAccount() {
  return fetchAccount(makeApiUrl(), makeDriver());
}

const patchAccount = (url, driver) => _ref => {
  let {
    account,
    id
  } = _ref;
  return driver({
    url: replaceUrl({
      url,
      idReplacement: id
    }),
    method: Method.PATCH,
    data: account
  });
};
function makePatchAccount() {
  return patchAccount(makeApiUrl(), makeDriver());
}

const changeEmail = (url, driver) => _ref => {
  let {
    email
  } = _ref;
  return driver({
    url: replaceUrl({
      url,
      restUrl: '/email-change/request'
    }),
    method: Method.POST,
    data: {
      email
    }
  });
};
function makeChangeEmail() {
  return changeEmail(makeApiUrl(), makeDriver());
}

const confirmEmailChange = (url, driver) => _ref => {
  let {
    verificationToken
  } = _ref;
  return driver({
    url: replaceUrl({
      url,
      restUrl: '/email-change/confirm'
    }),
    method: Method.POST,
    data: {
      verificationToken
    }
  });
};
function makeConfirmEmailChange() {
  return confirmEmailChange(makeApiUrl(), makeDriver());
}

const putAccountDetails = (url, driver) => _ref => {
  let {
    account
  } = _ref;
  return driver({
    url: replaceUrl({
      url,
      idReplacement: 'my',
      restUrl: '/details'
    }),
    method: Method.PUT,
    data: account
  });
};
function makePutAccountDetails() {
  return putAccountDetails(makeApiUrl(), makeDriver());
}

const fetchAccountDetails = (url, driver) => {
  return () => {
    return driver({
      url: replaceUrl({
        url,
        restUrl: '/details'
      })
    });
  };
};
function makeFetchAccountDetails() {
  return fetchAccountDetails(makeApiUrl(), makeDriver());
}

const cancelEmailChange = function (url) {
  let driver = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : makeDriver();
  return () => {
    return driver({
      url: replaceUrl({
        url,
        restUrl: '/email-change/cancel'
      }),
      method: Method.DELETE
    });
  };
};
function makeCancelChangeEmail() {
  return cancelEmailChange(makeApiUrl());
}

const isDev = process.env.NODE_ENV === 'development';
const usePatchAccount = patchAccount => {
  if (isDev) {
    // eslint-disable-next-line no-console
    console.warn('This function is deprecated and will be removed in the next major version. Please use usePutAccount instead');
  }
  const refetchAccount = useRefetchAccount$1();
  return useMutation(patchAccount, {
    onSuccess(data, vars) {
      refetchAccount(vars);
    }
  });
};
function useUsePatchAccount() {
  return usePatchAccount(makePatchAccount());
}

const useChangeEmail = (changeEmail, config) => {
  return useMutation(changeEmail, config);
};
function useUseChangeEmail(config) {
  return useChangeEmail(makeChangeEmail(), config);
}

const useConfirmEmailChange = (confirmEmailChange, config) => {
  return useMutation(confirmEmailChange, config);
};
function useUseConfirmEmailChange() {
  return useConfirmEmailChange(makeConfirmEmailChange());
}

const usePutAccountDetails = (putAccount, config) => {
  const cache = useQueryClient();
  return useMutation(putAccount, {
    ...config,
    onSuccess: (data, vars, context) => {
      cache.invalidateQueries(ACCOUNT_DETAILS);
      if ((config === null || config === void 0 ? void 0 : config.onSuccess) != null) {
        config.onSuccess(data, vars, context);
      }
    }
  });
};
function useUsePutAccountDetails(config) {
  return usePutAccountDetails(makePutAccountDetails(), config);
}

const useCancelEmailChange = (cancelEmailChange, config) => {
  return useMutation(cancelEmailChange, config);
};
function useUseCancelEmailChange(config) {
  return useCancelEmailChange(makeCancelChangeEmail(), config);
}

const changePassword = (url, driver) => data => driver({
  url: replaceUrl({
    url,
    restUrl: '/auth/password'
  }),
  method: Method.POST,
  data
});
function makeChangePassword() {
  return changePassword(makeApiUrl(), noContentDriver());
}

const checkPasswordStrength = (url, driver) => data => {
  return driver({
    url: `${url}/password/strength`,
    method: Method.POST,
    data
  });
};
function makeCheckPasswordStrength() {
  return checkPasswordStrength(apiUrl$1(), makeDriver());
}

const useChangePassword = (changePassword, config) => {
  return useMutation(changePassword, config);
};
function useUseChangePassword(config) {
  return useChangePassword(makeChangePassword(), config);
}

const createValidationMessage = intlNamespace => function (test) {
  let values = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return JSON.stringify({
    id: `${intlNamespace}.\${path}.${test}`,
    values: values.reduce((result, value) => {
      return {
        ...result,
        [value]: `\${${value}}`
      };
    }, {})
  });
};
const vMessage = createValidationMessage('ux.accounts-client.errors');
const firstName = string().required(vMessage('required'));
const lastName = string().required(vMessage('required'));
const email = string().email(vMessage('invalid')).required(vMessage('required'));
const telephone = string();
const address = object();

const useAccount = (fetch, id, opts) => {
  const authenticated = useIsAuthenticated();
  const fn = () => fetch({
    id
  });
  return useQuery([ACCOUNT, id], fn, {
    enabled: authenticated,
    staleTime: Infinity,
    ...opts
  });
};
function useUseAccount(id, opts) {
  return useAccount(makeFetchAccount(), id, opts);
}

const useMyAccount = opts => useUseAccount('my', opts);
var useMyAccount$1 = useMyAccount;

const isCompany = account => {
  var _account$type;
  return (account === null || account === void 0 ? void 0 : (_account$type = account.type) === null || _account$type === void 0 ? void 0 : _account$type.toLowerCase()) === 'company';
};
var isCompany$1 = isCompany;

const useMyAccountDetails = (fetch, opts) => {
  const authenticated = useIsAuthenticated();
  const fn = () => fetch();
  return useQuery(ACCOUNT_DETAILS, fn, {
    enabled: authenticated,
    staleTime: Infinity,
    ...opts
  });
};
function useUseMyAccountDetails(opts) {
  return useMyAccountDetails(makeFetchAccountDetails(), opts);
}

const fetchPreferences = (url, driver) => {
  return () => {
    return driver({
      url: replaceUrl({
        url,
        restUrl: '/preferences'
      })
    });
  };
};
function makeFetchPreferences() {
  return fetchPreferences(makeApiUrl(), makeDriver());
}

const usePreferencesQuery = (fetch, opts) => {
  const authenticated = useIsAuthenticated();
  return useQuery(PREFERENCES, fetch, {
    enabled: authenticated,
    staleTime: Infinity,
    ...opts
  });
};
const usePreferences = opts => {
  return usePreferencesQuery(makeFetchPreferences(), opts);
};

const usePasswordStrength = (checkPasswordStrength, password, config) => {
  const fetchFn = () => {
    // BE returns strength 0 for empty string, which doesn't really map to FE requirements.
    // Hooks should not be called conditionally, so we need to handle this here in the fetchFn
    if (password !== '') {
      return checkPasswordStrength({
        password
      });
    }
    return null;
  };
  return useQuery([PASSWORD_STRENGTH, password], fetchFn, config);
};
function useUsePasswordStrength(password, config) {
  return usePasswordStrength(makeCheckPasswordStrength(), password, config);
}

export { ACCOUNT, ACCOUNT_DETAILS, PASSWORD_STRENGTH, PREFERENCES, address, email, firstName, isCompany$1 as isCompany, lastName, telephone, useUseAccount as useAccount, useUseCancelEmailChange as useCancelEmailChange, useUseChangeEmail as useChangeEmail, useUseChangePassword as useChangePassword, useUseConfirmEmailChange as useConfirmEmailChange, useMyAccount$1 as useMyAccount, useUseMyAccountDetails as useMyAccountDetails, useUsePasswordStrength as usePasswordStrength, useUsePatchAccount as usePatchAccount, usePreferences, usePreferencesQuery, useUsePutAccountDetails as usePutAccountDetails, useRefetchAccount$1 as useRefetchAccount };
